import { Skeleton } from 'primeng/skeleton';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { ConfirmationService } from 'primeng/api';
import { StructuredLogger } from '@brighthub/logging';
import {
  CARE_PLAN_CASE_STATUS_LABEL,
  CarePlanCase,
  CarePlanCaseStatus,
  CARE_PLAN_MAX_ACTIVE_GOALS,
} from '../../care-plan.model';
import {
  CarePlanStore,
  CarePlanLiveWriteBlockedError,
  GoalSaveError,
  LiveGoalWrite,
} from '../../care-plan.store';
import { CarePlanAnalytics } from '../../analytics/care-plan-analytics';
import { caseApiErrorMessage } from '../api/case-api-error';
import { CARE_PLAN_FIXTURE_TAXONOMY } from '../../care-plan-fixture-taxonomy.token';

interface SelectOption {
  key: string;
  label: string;
}

export interface OpenCaseDialogData {
  store: CarePlanStore;
}

export interface OpenCaseDialogResult {
  createdCaseId?: string;
  navigateToCaseId?: string;
}

const STATUS_OPTIONS: { label: string; value: CarePlanCaseStatus }[] = [
  { label: 'Planned', value: 'planned' },
  { label: 'In progress', value: 'in_progress' },
  { label: 'Monitor', value: 'monitor' },
];

/**
 * "Open a case" — description, manual Domain/Objective (no AI suggestion,
 * D6), initial status, optional goal links + capture-under-cap, duplicate-case
 * warning.
 */
@Component({
  selector: 'bn-open-case-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Skeleton,
    FormsModule,
    Button,
    Checkbox,
    ConfirmDialog,
    Select,
    Textarea,
  ],
  providers: [ConfirmationService],
  templateUrl: './open-case-dialog.component.html',
  styleUrl: './open-case-dialog.component.scss',
})
export class OpenCaseDialogComponent {
  private readonly caseAction = inject(StructuredLogger).action(
    'care_plan.case.create',
  );
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config = inject(DynamicDialogConfig);
  private readonly _confirmation = inject(ConfirmationService);
  private readonly _analytics = inject(CarePlanAnalytics);
  private readonly _fixtureTaxonomy = inject(CARE_PLAN_FIXTURE_TAXONOMY, {
    optional: true,
  });
  private readonly _data = this._config.data as OpenCaseDialogData;

  protected readonly store = this._data.store;
  private readonly _openingGuid = this.store.sessionGuid();
  protected readonly domains = computed<SelectOption[]>(() =>
    this.store.isLive()
      ? this.store.domains().map((domain) => ({
          key: String(domain.domainId),
          label: domain.name,
        }))
      : [...(this._fixtureTaxonomy?.domains ?? [])],
  );
  protected readonly statusOptions = STATUS_OPTIONS;
  protected readonly statusLabel = CARE_PLAN_CASE_STATUS_LABEL;
  protected readonly maxActiveGoals = CARE_PLAN_MAX_ACTIVE_GOALS;

  readonly description = signal('');
  readonly domain = signal<string | null>(null);
  readonly objective = signal<string | null>(null);
  readonly status = signal<CarePlanCaseStatus>('in_progress');
  readonly goalIds = signal<string[]>([]);
  readonly draftGoals = signal<string[]>([]);

  /** Set once the FA has seen the duplicate warning and chosen to proceed anyway. */
  readonly duplicateConfirmed = signal(false);
  readonly submitAttempted = signal(false);
  readonly saveError = signal<string | null>(null);
  readonly saving = signal(false);
  readonly createdLiveCaseId = signal<string | null>(null);
  readonly ambiguousGoalCreate = signal(false);
  private remainingGoalWrites: LiveGoalWrite[] | null = null;
  protected readonly selectedDomainId = computed(() => {
    const domain = this.domain();
    return domain ? Number(domain) : null;
  });

  protected readonly objectiveOptions = computed<SelectOption[]>(() => {
    const domain = this.domain();
    if (!domain) return [];
    if (!this.store.isLive()) {
      return (this._fixtureTaxonomy?.objectives ?? []).filter(
        (objective) => objective.domainKey === domain,
      );
    }
    return this.store
      .objectiveTypesForDomain(Number(domain))
      .map((objective) => ({
        key: String(objective.objectiveTypeId),
        label: objective.name,
      }));
  });

  protected readonly duplicate = computed<CarePlanCase | null>(() => {
    const domain = this.domain();
    const objectiveKey = this.objective();
    if (!domain || !objectiveKey) return null;
    if (this.store.isLive()) {
      return this.store.findDuplicateByReference(
        Number(domain),
        Number(objectiveKey),
      );
    }
    return this.store.findDuplicate(domain, this._objectiveLabel(objectiveKey));
  });

  protected readonly descriptionError = computed(() => {
    if (!this.submitAttempted()) return null;
    const value = this.description().trim();
    if (value.length === 0)
      return "Tell us what's going on before opening a case";
    if (value.length > 250)
      return 'Description must be 250 characters or fewer';
    return null;
  });

  protected readonly domainObjectiveError = computed(() => {
    if (!this.submitAttempted()) return null;
    if (!this.domain()) return 'Select a domain';
    if (!this.objective()) return 'Select an objective type';
    return null;
  });

  protected readonly atCap = computed(
    () =>
      this.store.activeGoals().length + this.draftGoals().length >=
      this.maxActiveGoals,
  );

  protected readonly dirty = computed(
    () =>
      this.description().trim().length > 0 ||
      !!this.domain() ||
      !!this.objective() ||
      this.goalIds().length > 0 ||
      this.draftGoals().some((text) => text.trim().length > 0),
  );

  constructor() {
    effect(() => {
      if (!this.store.goalsEnabled()) {
        this.goalIds.set([]);
        this.draftGoals.set([]);
        this.remainingGoalWrites = null;
        this.ambiguousGoalCreate.set(false);
      }
    });
    effect(() => {
      if (this.store.sessionGuid() !== this._openingGuid) this._ref.close();
    });
  }

  onDomainChange(domain: string | null): void {
    this.domain.set(domain);
    this.objective.set(null);
    this.duplicateConfirmed.set(false);
    if (domain && this.store.isLive()) {
      void this.store.loadObjectiveTypes(Number(domain));
    }
  }

  onObjectiveChange(objective: string | null): void {
    this.objective.set(objective);
    this.duplicateConfirmed.set(false);
  }

  toggleGoal(goalId: string, checked: boolean): void {
    if (!this.store.goalsEnabled()) return;
    this.goalIds.update((ids) =>
      checked
        ? [...new Set([...ids, goalId])]
        : ids.filter((id) => id !== goalId),
    );
  }

  addDraftRow(): void {
    if (!this.store.goalsEnabled()) return;
    if (this.atCap()) {
      this._analytics.goalCapBlocked(this._openingGuid);
      return;
    }
    this.draftGoals.update((rows) => [...rows, '']);
  }

  setDraftText(index: number, text: string): void {
    if (!this.store.goalsEnabled()) return;
    this.draftGoals.update((rows) =>
      rows.map((r, i) => (i === index ? text : r)),
    );
  }

  removeDraftRow(index: number): void {
    this.draftGoals.update((rows) => rows.filter((_, i) => i !== index));
  }

  viewExistingCase(): void {
    const existing = this.duplicate();
    if (existing) {
      this._ref.close({
        navigateToCaseId: existing.id,
      } satisfies OpenCaseDialogResult);
    }
  }

  submit(): void {
    if (this.saving()) return;
    if (!this.store.goalsEnabled()) {
      this.goalIds.set([]);
      this.draftGoals.set([]);
      this.remainingGoalWrites = null;
      this.ambiguousGoalCreate.set(false);
    }
    if (this.ambiguousGoalCreate()) return;
    if (this.store.isLive() && !this._isOpeningClientCurrent()) {
      this.caseAction.begin().fail(null, 'submission', false, 'stale_state');
      this._ref.close();
      return;
    }
    if (this.createdLiveCaseId()) {
      void this._submitLive(
        this.description().trim(),
        Number(this.domain()),
        Number(this.objective()),
      );
      return;
    }
    if (
      this.store.isLive() &&
      (this.goalIds().length || this.draftGoals().some((text) => text.trim()))
    ) {
      if (!this.store.canManageGoals()) {
        this.caseAction
          .begin()
          .fail(null, 'submission', false, 'business_rule');
        this.saveError.set(
          'Wait for client goals to load before linking or capturing goals.',
        );
        return;
      }
      if (this.draftGoals().some((text) => text.trim().length > 200)) {
        this.saveError.set('Goals must contain 1–200 characters.');
        return;
      }
    }
    this.submitAttempted.set(true);
    const description = this.description().trim();
    const domain = this.domain();
    const objective = this.objective();
    if (
      description.length === 0 ||
      description.length > 250 ||
      !domain ||
      !objective
    ) {
      return;
    }
    const objectiveLabel = this._objectiveLabel(objective);
    if (this.duplicate() && !this.duplicateConfirmed()) {
      this.duplicateConfirmed.set(true);
      this._analytics.caseDuplicateWarningShown(domain, objectiveLabel);
      return;
    }
    this.saveError.set(null);
    if (this.store.isLive()) {
      void this._submitLive(description, Number(domain), Number(objective));
      return;
    }
    try {
      const clientId = this.store.sessionGuid();
      // Create the case with already-existing goal links first so a recoverable
      // save failure (D14) does not leave orphan draft goals in the store.
      const created = this.store.openCase({
        description,
        domain,
        objective: objectiveLabel,
        status: this.status(),
        goalIds: this.goalIds(),
      });

      const linkedGoalIds = [...created.goalIds];
      for (const text of this.draftGoals()) {
        const trimmed = text.trim();
        if (trimmed.length === 0 || trimmed.length > 200) continue;
        const createdGoal = this.store.addGoal(trimmed);
        if (createdGoal) {
          this.store.linkGoal(created.id, createdGoal.id);
          linkedGoalIds.push(createdGoal.id);
          if (clientId) {
            this._analytics.goalCreated(
              createdGoal.id,
              this.store.activeGoals().length,
              this._openingGuid,
            );
            this._analytics.caseGoalLinked(
              created.id,
              createdGoal.id,
              this._openingGuid,
            );
          }
        } else if (clientId) {
          this._analytics.goalCapBlocked(this._openingGuid);
        }
      }

      if (clientId) {
        this._analytics.caseOpened({
          caseId: created.id,
          domain,
          objectiveType: objectiveLabel,
          initialStatus: created.status,
          linkedGoalIds,
        });
        for (const goalId of created.goalIds) {
          this._analytics.caseGoalLinked(created.id, goalId, this._openingGuid);
        }
      }
      this._ref.close({
        createdCaseId: created.id,
      } satisfies OpenCaseDialogResult);
    } catch (err) {
      // The fixture path is retained for isolated Goal tests. No data is
      // committed on failure, so retrying with the same input remains safe.
      this.saveError.set(err instanceof Error ? err.message : 'Save failed');
    }
  }

  /** `objective` signal holds the taxonomy key (p-select optionValue); the store stores the label (EDD §4a). */
  private _objectiveLabel(objectiveKey: string): string {
    if (this.store.isLive()) {
      const domainId = Number(this.domain());
      const objectiveTypeId = Number(objectiveKey);
      const objective = this.store
        .objectiveTypesForDomain(domainId)
        .find((item) => item.objectiveTypeId === objectiveTypeId);
      return objective?.name || `Objective type ID ${objectiveTypeId}`;
    }
    return (
      this._fixtureTaxonomy?.objectives.find(
        (objective) => objective.key === objectiveKey,
      )?.label ?? objectiveKey
    );
  }

  private async _submitLive(
    description: string,
    domainId: number,
    objectiveTypeId: number,
  ): Promise<void> {
    const attempt = this.caseAction.begin();
    let stage: 'create_case' | 'save_goals' = 'create_case';
    let dispatched = false;
    const canEditAtSubmission = this.store.canEditLiveCases();
    this.saving.set(true);
    try {
      let createdId = this.createdLiveCaseId();
      if (!createdId) {
        dispatched = true;
        const created = await this.store.createLiveCase({
          description,
          domainId,
          objectiveTypeId,
          status: this.status(),
        });
        createdId = created.id;
        this.createdLiveCaseId.set(createdId);
        if (!this._isOpeningClientCurrent()) {
          attempt.succeed('create_case');
          this._ref.close();
          return;
        }
        const domain = this.store
          .domains()
          .find((item) => item.domainId === domainId);
        this._analytics.caseOpened({
          caseId: createdId,
          domain: domain?.code ?? String(domainId),
          objectiveType: this._objectiveLabel(String(objectiveTypeId)),
          initialStatus: created.status,
          linkedGoalIds: [],
        });
      }
      const caseId = createdId;
      if (!this.store.goalsEnabled()) this.remainingGoalWrites = [];
      if (!this.remainingGoalWrites) {
        this.remainingGoalWrites = [
          ...this.goalIds().map((goalId) => ({
            kind: 'link' as const,
            goalId,
            caseId,
          })),
          ...this.draftGoals()
            .filter((text) => text.trim())
            .map((description) => ({
              kind: 'create' as const,
              description,
              caseId,
            })),
        ];
      }
      if (this.remainingGoalWrites.length) {
        stage = 'save_goals';
        dispatched = true;
        await this.store.saveLiveGoals(
          [...this.remainingGoalWrites],
          (change, id) => {
            this.remainingGoalWrites?.shift();
            if (change.kind === 'create' && id) {
              this._analytics.caseGoalLinked(caseId, id, this._openingGuid);
            } else if (change.kind === 'link')
              this._analytics.caseGoalLinked(
                caseId,
                change.goalId,
                this._openingGuid,
              );
          },
          false, // The case submission owns this multi-step FA outcome.
        );
      }
      attempt.succeed(stage);
      this._ref.close({
        createdCaseId: createdId,
      } satisfies OpenCaseDialogResult);
    } catch (error) {
      // The case already committed. Disabling goals must not block finishing it.
      if (this.createdLiveCaseId() && !this.store.goalsEnabled()) {
        attempt.succeed('create_case');
        this.closeWithCreatedCase();
        return;
      }
      attempt.fail(
        error,
        stage,
        dispatched,
        error instanceof CarePlanLiveWriteBlockedError
          ? canEditAtSubmission
            ? 'business_rule'
            : 'permission_denied'
          : undefined,
      );
      this.ambiguousGoalCreate.set(
        error instanceof GoalSaveError && error.ambiguousCreate,
      );
      this.saveError.set(
        (this.createdLiveCaseId()
          ? 'The case was opened. Some goal changes may still need attention. '
          : '') + caseApiErrorMessage(error, 'The case could not be opened.'),
      );
    } finally {
      this.saving.set(false);
    }
  }

  tryClose(): void {
    if (this.saving()) return;
    if (!this.dirty()) {
      this.closeWithCreatedCase();
      return;
    }
    this._confirmation.confirm({
      message:
        'You have unsaved changes — are you sure you want to discard them?',
      header: 'Unsaved changes',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Discard',
      rejectLabel: 'Stay',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.closeWithCreatedCase(),
    });
  }

  private closeWithCreatedCase(): void {
    const id = this.createdLiveCaseId();
    if (id) this._ref.close({ createdCaseId: id });
    else this._ref.close();
  }

  private _isOpeningClientCurrent(): boolean {
    return (
      this.store.sessionGuid() === this._openingGuid &&
      (!this.store.isLive() || this.store.canCreateLiveCase())
    );
  }
}
